import { convertFormatDateTimeToDisplayDateTime, convertFormatDateToDisplayDate } from "../utils/time-utils";

const regexp = /\${(.*?)\}/g;
const fallbackLanguage = "en";
let commonTranslations;
let backendTranslations;
let navigationTranslations;
let viewTranslations;
let language = fallbackLanguage;

const LOCALE_MAP = {
	en: "en-GB",
	esp: "es-ES",
	fr: "fr-FR",
};

const getIntlLocale = (lang) => LOCALE_MAP[lang] ?? "en-GB";

export const translateDate = (date) => {
	const slicedDate = convertFormatDateToDisplayDate(date);
	if (slicedDate.toString() !== "Invalid Date") {
		return new Intl.DateTimeFormat(getIntlLocale(language), {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		}).format(slicedDate);
	}
	return "-";
};
export const translateDateTime = (date) => {
	const slicedDate = convertFormatDateTimeToDisplayDateTime(date);

	if (slicedDate.toString() !== "Invalid Date") {
		return new Intl.DateTimeFormat(getIntlLocale(language), {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			hour12: false,
		})
			.format(slicedDate)
			.replace(",", "");
	}
	return "-";
};

export const translateDateAndTime = (dateString) => {
	if (typeof dateString !== "string" || !dateString.trim() || dateString.split(" ").length < 3) {
		console.error("Wrong input format");
		return "-";
	}

	try {
		const [datePart, timePart, ...zoneParts] = dateString.split(" ");
		const backendTimeZone = zoneParts.join(" ");
		const [year, month, day] = datePart.split("-").map(Number);
		const [hour, minute] = timePart.split(":").map(Number);

		const initialUtcCandidate = Date.UTC(year, month - 1, day, hour, minute);
		const dateForOffsetCalculation = new Date(initialUtcCandidate);

		const backendFormatterCheck = new Intl.DateTimeFormat("en-US", {
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
			timeZone: backendTimeZone,
		});
		const backendTimeParts = backendFormatterCheck.formatToParts(dateForOffsetCalculation);
		const backendHourFormatted = parseInt(backendTimeParts.find((p) => p.type === "hour")?.value, 10);
		const backendMinuteFormatted = parseInt(backendTimeParts.find((p) => p.type === "minute")?.value, 10);

		let offsetHours = backendHourFormatted - hour;
		const offsetMinutes = backendMinuteFormatted - minute;

		if (offsetHours < -12) {
			offsetHours += 24;
		}
		if (offsetHours > 12) {
			offsetHours -= 24;
		}

		const totalOffsetMilliseconds = (offsetHours * 60 + offsetMinutes) * 60 * 1000;
		const finalUtcMilliseconds = initialUtcCandidate - totalOffsetMilliseconds;
		const preciseUtcMoment = new Date(finalUtcMilliseconds);

		if (Number.isNaN(preciseUtcMoment.getTime())) {
			return "-";
		}

		const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		const locale = getIntlLocale(language);

		const dateFormatted = new Intl.DateTimeFormat(locale, {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			timeZone: userTimeZone,
		}).format(preciseUtcMoment);

		const timeFormatted = new Intl.DateTimeFormat(locale, {
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
			timeZone: userTimeZone,
		}).format(preciseUtcMoment);

		return `${dateFormatted}, ${timeFormatted}`;
	} catch (error) {
		console.error("translateDateAndTime error:", error);
		return "-";
	}
};

function replaceParams(translation, groups, params) {
	if (!Array.isArray(groups) || groups.length === 0 || !params || typeof params !== "object") {
		return translation;
	}
	let tmpTranslation = translation;
	for (let i = 0, size = groups.length; i < size; ++i) {
		const placeholder = groups[i][0] || "";
		const placeholderName = groups[i][1] || "";
		let replaceValue = placeholder ?? "";
		if (placeholder && placeholderName) {
			replaceValue = params[placeholderName] ?? "";
		}
		if (placeholderName.startsWith("trsDate")) {
			replaceValue = translateDate(replaceValue);
		}
		tmpTranslation = tmpTranslation.replace(placeholder, replaceValue);
	}
	return tmpTranslation;
}

export function translate(key, params) {
	if (!key) {
		return "#INVALID";
	}
	let translation;
	if (key.startsWith("navigation:")) {
		translation = navigationTranslations?.[key] || "";
	} else if (key.startsWith("common:")) {
		translation = commonTranslations?.[key] || "";
	} else if (key.startsWith("backend:")) {
		translation = backendTranslations?.[key] || "";
	} else {
		translation = viewTranslations?.[key] || "";
	}
	if (!translation) {
		return key;
	}
	if (translation.search(regexp) >= 0) {
		const groups = [...translation.matchAll(regexp)];
		translation = replaceParams(translation, groups, params);
	}
	return translation;
}

export const loadTranslation = async (lang) => {
	language = lang;
	try {
		const promiseCommonTranslations = import("../locales/common-translations/translation");
		const promiseBackendTranslations = import("../locales/backend-translations/translation");
		const commonTrs = (await promiseCommonTranslations) || {};
		const backendTrs = (await promiseBackendTranslations) || {};
		commonTranslations = commonTrs?.default?.[lang] || null;
		backendTranslations = backendTrs?.default?.[lang] || null;
	} catch (err) {
		console.error(err);
		throw new Error(`Error while loading translations${err}`);
	}
};

export const loadNavTranslation = async (lang) => {
	language = lang;
	try {
		const promiseNavigationTranslations = import("../../navigation/locales/translation");
		const navigationTrs = (await promiseNavigationTranslations) || {};
		navigationTranslations = navigationTrs?.default?.[lang] || null;
	} catch (err) {
		console.error(err);
		throw new Error(`Error while loading nav translations${err}`);
	}
};

export const loadViewTranslation = async (lang, localesPath) => {
	language = lang;
	if (!localesPath) {
		throw new Error("Missing view localesPath");
	}
	try {
		const viewTrs = (await import(`../../views${localesPath}/translation`)) || {};
		viewTranslations = { ...viewTranslations, ...(viewTrs?.default?.[lang] || {}) };
	} catch (err) {
		console.error(err);
		throw new Error(`Error while loading view translations${err}`);
	}
};

export const addTranslations = (addtionnalTranslations, lang) => {
	language = lang;
	viewTranslations = { ...(viewTranslations || {}), ...addtionnalTranslations[lang] };
	return viewTranslations;
};

export const translateEnum = (type, value, param = {}) =>
	translate(`common:enum.${type}.${value.replaceAll("_", "-")}`, param);

export const translateElapsed = ({ value, unitKey }) =>
	translate("common:elapsed-time", {
		unit: translateEnum("time-unit", `${unitKey?.toLowerCase()}${value > 1 ? ".plural" : ".singular"}`),
		value,
	});

export const translateEnumParam = (param, value = "") => {
	const pFormatted = param?.toLowerCase();
	const vFormatted = value?.toLowerCase();
	return translateEnum("params", value ? `${pFormatted}.${vFormatted}` : pFormatted);
};
export const translateEnumParamTooltip = (param, value = "") =>
	translateEnumParam(param, value ? `${value}.tooltip` : "tooltip");
export const translateEnumStatus = (value) => translateEnum("status", value?.toLowerCase());
export const translateEnumReqStatus = (value) => translateEnum("req-status", value?.toLowerCase());
export const translateEnumProjectRole = (value) => translateEnum("project-role", value?.toLowerCase());
export const translateEnumProjectRoleDescription = (value) =>
	translateEnum("project-role.description", value?.toLowerCase());
export const translateEnumDocumentStatus = (value) => translateEnum("document-status", value?.toLowerCase());
export const translateEnumDocumentUserRole = (value) => translateEnum("document.user-role", value?.toLowerCase());
export const translateEnumCoverageStatus = (value) => translateEnum("coverage-status", value?.toLowerCase());
export const translateEnumQuestionStatus = (value) => translateEnum("question-status", value?.toLowerCase());
export const translateEnumTemplatetype = (value, param) => translateEnum("template-type", value?.toLowerCase(), param);
export const translateEnumKeywordTitle = (value) => translateEnum("keywords.title", value?.toLowerCase());
export const translateEnumInformationAttribute = (value) =>
	translateEnum("filters.information-attribute", value?.toLowerCase());
export const translateEnumContentType = (value) => translateEnum("filters.content-type", value?.toLowerCase());
export const translateEnumClassification = (value) => translateEnum("filters", `req-${value?.toLowerCase()}.title`);
export const translateEnumVersionStatus = (value) => translateEnum("version-status", value?.toLowerCase());
export const translateEnumSearchCardType = (value) => translateEnum("search-card-type", value?.toLowerCase());
export const translateEnumExportDialog = (value) => translateEnum("export-dialog", value?.toLowerCase());
export const translateEnumNotificationFrequency = (value) =>
	translateEnum("notification-settings.frequency", value?.toLowerCase());
